export const FORBIDDEN_FILE_NAME_CHARACTERS = ['<', '>', ':', '"', '/', '\t', '|', '?', '*', '+', '#'] as const;

export interface FileNameValidationResult {
  invalidNames: string[];
  duplicateNamesInSelection: string[];
  duplicateNamesWithExisting: string[];
  messages: string[];
}

export interface ValidateFileNamesOptions {
  existingNames?: string[];
  forbiddenCharacters?: readonly string[];
}

export function normalizeFileName(fileName: string): string {
  if (!fileName) {
    return '';
  }
  
  return fileName.trim().toLocaleLowerCase();
}

export function getInvalidCharacters(
  fileName: string,
  forbiddenCharacters: readonly string[] = FORBIDDEN_FILE_NAME_CHARACTERS
): string[] {
  return forbiddenCharacters.filter(character => fileName.includes(character));
}

export function isFileNameValid(
  fileName: string,
  forbiddenCharacters: readonly string[] = FORBIDDEN_FILE_NAME_CHARACTERS
): boolean {
  return getInvalidCharacters(fileName, forbiddenCharacters).length === 0;
}

export function getFileNameValidationMessage(
  fileName: string,
  options: ValidateFileNamesOptions = {},
  selectedNames: string[] = []
): string | null {
  const forbiddenCharacters = options.forbiddenCharacters ?? FORBIDDEN_FILE_NAME_CHARACTERS;

  if (!isFileNameValid(fileName, forbiddenCharacters)) {
    return `Le fichier "${fileName}" contient un caractère interdit (${getInvalidCharacters(fileName, forbiddenCharacters).join(' ')}).`;
  }

  const normalizedName = normalizeFileName(fileName);

  if (selectedNames.some(name => normalizeFileName(name) === normalizedName)) {
    return `Le nom de fichier doit être unique : "${fileName}" est déjà présent dans votre sélection.`;
  }

  if ((options.existingNames ?? []).some(name => normalizeFileName(name) === normalizedName)) {
    return `Le fichier "${fileName}" existe déjà dans les documents du projet.`;
  }

  return null;
}

export function validateFileNames(
  fileNames: string[],
  options: ValidateFileNamesOptions = {}
): FileNameValidationResult {
  const forbiddenCharacters = options.forbiddenCharacters ?? FORBIDDEN_FILE_NAME_CHARACTERS;
  const invalidNames = fileNames.filter(name => !isFileNameValid(name, forbiddenCharacters));
  const duplicateNamesInSelection = getDuplicateNames(fileNames);
  const duplicateNamesWithExisting = fileNames.filter((name, index, names) => {
    if (names.indexOf(name) !== index) {
      return false;
    }

    return (options.existingNames ?? []).some(existingName => normalizeFileName(existingName) === normalizeFileName(name));
  });

  const messages = [
    ...invalidNames.map(name => `Le fichier "${name}" contient un caractère interdit (${getInvalidCharacters(name, forbiddenCharacters).join(' ')}).`),
    ...duplicateNamesInSelection.map(name => `Le nom de fichier doit être unique : "${name}" est présent plusieurs fois dans la sélection.`),
    ...duplicateNamesWithExisting.map(name => `Le fichier "${name}" existe déjà dans les documents du projet.`),
  ];

  return {
    invalidNames,
    duplicateNamesInSelection,
    duplicateNamesWithExisting,
    messages: [...new Set(messages)],
  };
}

function getDuplicateNames(fileNames: string[]): string[] {
  const counts = new Map<string, string[]>();

  fileNames.forEach(name => {
    const normalizedName = normalizeFileName(name);
    const existing = counts.get(normalizedName) ?? [];
    existing.push(name);
    counts.set(normalizedName, existing);
  });

  return Array.from(counts.values())
    .filter(names => names.length > 1)
    .map(names => names[0]);
}
